import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import HumanTaskDisplay from './dsl-components/HumanTaskDisplay';
import HumanTaskPhoneDialer from './dsl-components/HumanTaskPhoneDialer';
import { AppTab } from './appLayoutConfig';

interface HumanTaskWrapperProps {
  taskId: string;
  threadId?: string;
  tabId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

interface TaskData {
  id: string;
  taskType: 'phone_call' | 'web_action';
  title: string;
  description: string;
  status: string;
  taskDetails: any;
}

/** Tabs to show for phone call tasks */
const PHONE_CALL_TABS: AppTab[] = [
  { id: 'details', label: 'Task Details' },
  { id: 'phone', label: 'Phone Dialer' },
];

/** Tabs to show for web action tasks */
const WEB_ACTION_TABS: AppTab[] = [{ id: 'details', label: 'Task Details' }];

/**
 * Wrapper component that fetches human task data and renders content.
 * Calls onTabsReady to tell CanvasAssets which tabs to show in the header.
 * - Phone call tasks: Shows task details and phone dialer tabs
 * - Web action tasks: Shows only task details tab
 */
const HumanTaskWrapper: React.FC<HumanTaskWrapperProps> = ({
  taskId,
  threadId,
  tabId = 'details',
  updateThreadViewVisualDescription,
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [taskData, setTaskData] = useState<TaskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTabsRef = useRef<string>('');

  const fetchTask = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/human-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.task) {
        const task = response.data.task;
        setTaskData({
          id: task.id,
          taskType: task.taskType,
          title: task.title,
          description: task.description,
          status: task.status,
          taskDetails: task.taskDetails,
        });

        // Update visual description if callback provided
        if (updateThreadViewVisualDescription) {
          updateThreadViewVisualDescription(`Human Task: ${task.title}`);
        }
      } else {
        setError('Failed to load task');
      }
    } catch (err) {
      console.error('Error fetching human task:', err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId, getAccessTokenSilently, updateThreadViewVisualDescription]);

  // Reset state when taskId changes
  useEffect(() => {
    setLoading(true);
    setTaskData(null);
    setError(null);
    lastTabsRef.current = ''; // Reset so onTabsReady will be called for new task
    fetchTask();
  }, [taskId, fetchTask]);

  // Notify parent of tabs when task data changes
  useEffect(() => {
    if (!taskData || !onTabsReady) return;

    const tabs =
      taskData.taskType === 'phone_call' ? PHONE_CALL_TABS : WEB_ACTION_TABS;
    const tabsKey = JSON.stringify(tabs);

    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [taskData, onTabsReady]);

  if (loading) {
    return (
      <div className='human-task-wrapper loading'>
        <p>Loading task...</p>
      </div>
    );
  }

  if (error || !taskData) {
    return (
      <div className='human-task-wrapper error'>
        <p>{error || 'Task not found'}</p>
      </div>
    );
  }

  // Render content based on selected tab
  switch (tabId) {
    case 'phone':
      // Only render phone dialer for phone call tasks
      if (taskData.taskType !== 'phone_call') {
        return (
          <HumanTaskDisplay
            taskId={taskData.id}
            taskType={taskData.taskType}
            taskTypeLabel='Web Action'
            title={taskData.title}
            description={taskData.description}
            taskDetails={taskData.taskDetails}
            status={taskData.status}
          />
        );
      }
      return (
        <HumanTaskPhoneDialer
          taskId={taskData.id}
          phoneNumber={taskData.taskDetails?.phoneNumber || ''}
          contactName={taskData.taskDetails?.contactName}
        />
      );

    case 'details':
    default:
      return (
        <HumanTaskDisplay
          taskId={taskData.id}
          taskType={taskData.taskType}
          taskTypeLabel={
            taskData.taskType === 'phone_call' ? 'Phone Call' : 'Web Action'
          }
          title={taskData.title}
          description={taskData.description}
          taskDetails={taskData.taskDetails}
          status={taskData.status}
        />
      );
  }
};

export default HumanTaskWrapper;
